import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { combineLatest, forkJoin, from, Observable, of, Subject } from 'rxjs';
import {
    concatMap,
    delay,
    finalize,
    map,
    mergeMap,
    shareReplay,
    take,
    tap
} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ThumediDatabase } from '../core/abstractions/thumedi-database';
import { ThumediDatabaseHelper } from '../core/abstractions/thumedi-database.helper';
import { ThumediRepository } from '../core/abstractions/thumedi.repository';
import { TableStateModel } from '../models/table-state.model';
import { UnsyncedChangeModel } from '../models/unsynced-change.model';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { OnlineStatusService } from './online-status.service';
import { TableStateResource } from './resources/table-state.resource';
import { SyncStorage } from './storages/sync.storage';
import { catchError, takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class SyncServiceNew {
    private swUpdate = inject(SwUpdate);
    private database = inject(ThumediDatabase);
    private dbHelper = inject(ThumediDatabaseHelper);
    private onlineStatusService = inject(OnlineStatusService);
    private syncStorage = inject(SyncStorage);
    private authService = inject(AuthService);
    private alertService = inject(AlertService);
    private tableStateResource = inject(TableStateResource);
    private router = inject(Router);

    protected bookingsWorker: Worker;
    protected customersWorker: Worker;

    isAppLocked = false;
    readonly progressChanged = new Subject<string>();
    readonly hasSyncFinished = new Subject<boolean>();
    private _isSyncing = false;
    private _serverUpdates: Observable<any>[] = [];
    private _clientUpdates: Observable<any>[] = [];
    private _cancelSync = new Subject<void>();

    start(bookingWorker: Worker, customersWorker: Worker): void {
        this.bookingsWorker = bookingWorker;
        this.customersWorker = customersWorker;

        this.database
            .setup()
            .then()
            .catch(error => {
                console.error('Database setup failed:', error);
                // Consider showing user notification or implementing retry logic
            });
    }

    validateSyncRequirement(): void {
        if (this._isSyncing) {
            return;
        }

        const lastSync = this.syncStorage.getLastSync();

        if (!lastSync) {
            this.authService.logout();
            return;
        }

        const now = new Date();
        const dateDiff = Math.abs(now.getTime() - lastSync.getTime());
        const daysWithoutUpdate = Math.ceil(dateDiff / (1000 * 3600 * 24));
        const warningThreshold =
            environment.notSyncedMaxDays -
            environment.notSyncedPreMaxDaysWarning;

        if (
            daysWithoutUpdate >= warningThreshold &&
            daysWithoutUpdate < environment.notSyncedMaxDays
        ) {
            // Warning phase: we tell the user if no update is done soon, he'll lose access.
            const remainingSyncDays =
                environment.notSyncedMaxDays - daysWithoutUpdate;
            this.alertService.showAlert(
                `In spätestens ${remainingSyncDays} Tagen ist ein Datenabgleich notwendig.`
            );
        } else if (daysWithoutUpdate >= environment.notSyncedMaxDays) {
            // Block phase: we prevent the user from accessing the app until syncing.
            this.isAppLocked = true;

            this.alertService.showError(
                `Kein Datenabgleich seit ${daysWithoutUpdate} Tagen.
                Sie können die App danch erst nutzen, wenn Sie erneut synchronisieren.`
            );
            this.authService.logout();
        }
    }

    sync(): void {
        this.onlineStatusService.isOnline$.pipe(take(1)).subscribe(r => {
            if (r) {
                this.performSync();
            }
        });
    }

    resetSync(): void {
        from(
            this.alertService.showCustomConfirm(
                'Do you want to reset sync process?',
                'Warning!',
                'Wiederholen',
                'Abbrechen'
            )
        )
            .pipe(
                take(1),
                mergeMap(res => {
                    if (res) {
                        return this.clearDatabase().pipe(
                            tap(() => {
                                this._cancelSync.next();
                                window.location.reload();
                            })
                        );
                    }

                    return of(null);
                })
            )
            .subscribe();
    }

    checkFullSyncNeeded() {
        return this.onlineStatusService.isOnline$.pipe(take(1)).pipe(
            mergeMap(r => {
                if (r) {
                    return this.checkFullSyncRequired();
                }

                return of(null);
            })
        );
    }

    private performSync(): void {
        if (this._isSyncing) {
            return;
        }

        this._isSyncing = true;

        this.progressChanged.next('App wird geladen');

        if (environment.production) {
            this.swUpdate.checkForUpdate();
        }

        this.progressChanged.next(
            'Validierung von Synchronisationsanforderungen'
        );

        this.validateSync()
            .pipe(
                takeUntil(this._cancelSync),
                take(1),
                mergeMap(() => {
                    if (
                        this._serverUpdates.length > 0 &&
                        this._clientUpdates.length > 0
                    ) {
                        return forkJoin(this._serverUpdates).pipe(
                            concatMap(() => forkJoin(this._clientUpdates)),
                            shareReplay()
                        );
                    } else if (this._serverUpdates.length > 0) {
                        return forkJoin(this._serverUpdates).pipe(
                            shareReplay()
                        );
                    } else if (this._clientUpdates.length > 0) {
                        return forkJoin(this._clientUpdates).pipe(
                            shareReplay()
                        );
                    } else {
                        return of(null);
                    }
                }),
                shareReplay(),
                catchError(_err => {
                    return this.errorSync();
                }),
                finalize(() => this.finalizeSync())
            )
            .subscribe();

        this.progressChanged.next('Synchronisiere...');
    }

    private validateSync() {
        return combineLatest([
            this.tableStateResource.getCurrent(),
            this.dbHelper.getAll('unsyncedChanges'),
            this.dbHelper.getAll('tableStates')
        ]).pipe(
            mergeMap(res => {
                const tableStates = res[0];
                const unsyncedChanges = res[1] as UnsyncedChangeModel[];
                const tableStatesLocal = res[2] as TableStateModel[];

                const count: Observable<{
                    tableName: any;
                    count: number;
                }>[] = [];

                for (const serverState of tableStates) {
                    count.push(this.hasLocalRecords(serverState.id));
                }
                return forkJoin(count).pipe(
                    map(r => {
                        r.forEach(x => {
                            const unsyncedChangesByTable =
                                unsyncedChanges.filter(
                                    un => un.tableName == x.tableName
                                );

                            const serverState = tableStates.find(
                                st => st.id == x.tableName
                            );

                            const localState = tableStatesLocal.find(
                                ls => ls.id === x.tableName
                            );

                            // If the local count is 0, we need to sync from server.
                            if (
                                !x.count ||
                                x.count === 0 ||
                                (localState.lastLocalUpdate &&
                                    new Date(serverState.modified) >
                                        new Date(localState.lastLocalUpdate))
                            ) {
                                const isFirstLoad =
                                    !localState?.lastLocalUpdate;

                                this._serverUpdates.push(
                                    this.dbHelper
                                        .getBackendResourceByTable(x.tableName)
                                        .syncFromServer(
                                            !!localState?.lastLocalUpdate
                                                ? new Date(
                                                      localState.lastLocalUpdate
                                                  )
                                                : undefined,
                                            isFirstLoad
                                        )
                                        .pipe(
                                            concatMap(res => {
                                                this.progressChanged.next(
                                                    `Lade ${x.tableName}`
                                                );

                                                return this.dbHelper.editRange(
                                                    x.tableName,
                                                    res.records,
                                                    true
                                                );
                                            }),
                                            tap(() => {
                                                this.progressChanged.next(
                                                    `Synchronisiere ${x.tableName}`
                                                );
                                            }),
                                            catchError(_err => {
                                                return this.errorSync();
                                            })
                                        )
                                );
                            }

                            if (unsyncedChangesByTable.length > 0) {
                                this._clientUpdates.push(
                                    this.dbHelper
                                        .getBackendResourceByTable(x.tableName)
                                        .syncToServerNew(unsyncedChangesByTable)
                                        .pipe(
                                            concatMap(res => {
                                                this.progressChanged.next(
                                                    `Speichere ${x.tableName}`
                                                );

                                                if (res) {
                                                    return this.dbHelper.deleteRange(
                                                        'unsyncedChanges',
                                                        unsyncedChangesByTable
                                                    );
                                                }

                                                return of(null);
                                            }),
                                            catchError(_err => {
                                                return this.errorSync();
                                            })
                                        )
                                );
                            }
                        });
                    }),
                    concatMap(() =>
                        this.dbHelper.editRange(
                            'tableStates',
                            tableStates.map(x => {
                                x.lastLocalUpdate = x.modified;

                                return x;
                            })
                        )
                    )
                );
            })
        );
    }

    private hasLocalRecords<T extends keyof ThumediRepository>(tableName: T) {
        return this.dbHelper.count(tableName).pipe(
            map(r => {
                return { tableName, count: r };
            })
        );
    }

    private finalizeSync(): void {
        this.progressChanged.next('Abschluss der Synchronisierung');

        if (!this.syncStorage.lastFullSyncExists()) {
            this.syncStorage.saveLastFullSync(new Date());

            // We should only perfom this operation if is the first sync
            this.startBackgroundBookingsSync();
        }

        this.syncStorage.saveLastSync(new Date());

        this.hasSyncFinished.next(true);

        this._serverUpdates = [];
        this._clientUpdates = [];
        this.isAppLocked = false;
        this._isSyncing = false;
    }

    private checkFullSyncRequired() {
        if (this.syncStorage.lastFullSyncExists()) {
            return this.tableStateResource
                .validateFullSync(this.syncStorage.getLastFullSync())
                .pipe(
                    mergeMap(shouldFullSync => {
                        if (shouldFullSync) {
                            return this.prepareFullSync();
                        }

                        return of(null);
                    })
                );
        }

        return of(null);
    }

    private prepareFullSync() {
        return from(
            this.alertService.showCustomConfirm(
                'Es kann sein, dass Sie ihre ungespeicherten Daten verlieren',
                'Vollständige Synchronisierung erforderlich',
                'Ok'
            )
        ).pipe(
            concatMap(() => {
                this.syncStorage.eraseLastFullSync();

                return this.clearDatabase().pipe(
                    concatMap(() => from(this.router.navigate(['data-sync'])))
                );
            })
        );
    }

    private errorSync() {
        return from(
            this.alertService.showCustomConfirm(
                'Bei der Synchronisierung ist ein Fehler aufgetreten. Möchten Sie es erneut versuchen?',
                'Ein Fehler trat auf',
                'Wiederholen',
                'Abbrechen'
            )
        ).pipe(
            mergeMap(res => {
                if (res && res.isConfirmed) {
                    return this.clearDatabase().pipe(
                        finalize(() => this.sync())
                    );
                }

                return of(null);
            })
        );
    }

    private clearDatabase() {
        return forkJoin([
            from(this.dbHelper.getTable('bookings').clear()),
            from(this.dbHelper.getTable('customers').clear()),
            from(this.dbHelper.getTable('allocations').clear()),
            from(this.dbHelper.getTable('providers').clear()),
            from(this.dbHelper.getTable('todos').clear()),
            from(this.dbHelper.getTable('unsyncedChanges').clear()),
            from(this.dbHelper.getTable('templates').clear()),
            from(this.dbHelper.getTable('tableStates').clear())
        ]);
    }

    private startBackgroundBookingsSync() {
        if (!this.bookingsWorker || !this.customersWorker) {
            return;
        }

        of({})
            .pipe(
                take(1),
                delay(1000),
                tap(() => {
                    this.bookingsWorker.postMessage({
                        key: 'START'
                    });

                    this.customersWorker.postMessage({
                        key: 'START'
                    });
                })
            )
            .subscribe();
    }
}
